import * as Dialog from '@radix-ui/react-dialog'
import { useTranslation } from '../localization/useTranslation'
import { usePortalContainer } from '../providers/portal'
import { useAuthStore } from '../store/auth'
import { IconX } from '../ui/icons'
import { StepArt } from '../ui/stepArt'
import { OverlayReporter } from './OverlayReporter'

/**
 * First-run "How trade-in works" explainer (C2S-883). Three steps — find / send / reward — matching
 * the login page's how-it-works section and the Confirm page's pack → drop off → reward structure, so
 * the customer meets the same story wherever they enter.
 *
 * The reward step's wording is strategy-variant (`intro.step3Body` is a StrategyText), resolved by the
 * localization layer from the brand's `voucherStrategy` — this component stays strategy-blind. The
 * reward *word* comes from the subject lexicon ({reward}/{rewardPoss}) so brands running gift cards or
 * their own systems can override it via `tradeInConfig.terms[locale]` instead of us saying "voucher".
 *
 * Open/close is owned by the caller (FlowLayout gates first-run; the topbar menu re-opens it).
 * `onAccept` fires ONLY for the explicit "Got it" button — a scrim click or Escape closes the dialog
 * through `onOpenChange` alone, without acknowledging it, so the customer sees it again next visit.
 */
export function IntroDialog({
  open,
  onOpenChange,
  onAccept,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The customer acknowledged the dialog — distinct from merely closing it. */
  onAccept: () => void
}) {
  const { t } = useTranslation()
  const container = usePortalContainer()
  const brandName = useAuthStore((s) => s.config.brandName)
  const brand = brandName?.trim() || t('terms.brandFallback')

  const steps = ['step1', 'step2', 'step3']

  return (
    // modal={false}: Radix's scroll-lock (react-remove-scroll) blocks the mouse wheel inside our
    // shadow root (it can't resolve the wheel target across the boundary). Disabling it restores
    // native wheel scrolling; scrim/Escape/outside-click still close it. (See TermsDialog.)
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <Dialog.Portal container={container ?? undefined}>
        {/* Plain scrim, not Dialog.Overlay: Radix renders Overlay as null when modal={false}. As a
            direct Portal child it's still open-state gated, and clicking it closes the dialog. */}
        <div className="modal-scrim" aria-hidden />
        <Dialog.Content className="modal" aria-describedby={undefined}>
          <OverlayReporter />
          <div className="modal-head">
            <Dialog.Title asChild>
              <h2>{t('intro.title')}</h2>
            </Dialog.Title>
            <Dialog.Close className="icon-btn" aria-label={t('intro.close')}>
              <IconX />
            </Dialog.Close>
          </div>

          <div className="modal-body">
            <p className="intro-lead">{t('intro.lead', { brand })}</p>
            <ol className="intro-steps">
              {steps.map((key, i) => (
                <li className="intro-step" key={key}>
                  <StepArt index={i} className="intro-step-ic" fit />
                  <div>
                    <h3>{t(`intro.${key}Title`)}</h3>
                    <p>{t(`intro.${key}Body`)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="modal-foot intro-foot">
            <Dialog.Close asChild>
              <button type="button" className="btn btn-primary" onClick={onAccept}>
                {t('intro.cta')}
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
